// src/pages/Jobs.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiPlus, FiBriefcase, FiFilter, FiEdit, FiTrash2, FiChevronRight, FiX, FiSearch, FiGrid, FiList, FiSliders, FiArrowUp, FiArrowDown, FiExternalLink, FiGitMerge, FiAlertTriangle, FiCheckCircle, FiClock, FiAlertCircle } from 'react-icons/fi';
import { getJobs, deleteJob, getJobMergePreview, mergeJob } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import JobForm from '../components/JobForm';
import { buildQPublicUrl, hasQPublicUrl } from '../utils/parcelUtils';

const JOBS_PER_PAGE = 50;

// Georgia 811 status for a row. Short word in the chip, the full sentence on hover.
// Null from the server when 811 doesn't apply (or field work is over).
const LOCATE_811_CHIP = {
  ok: { text: 'Good', bg: '#ecfdf5', border: '#a7f3d0', color: '#047857', dot: '#10b981' },
  waiting: { text: 'Waiting', bg: '#fffbeb', border: '#fde68a', color: '#92400e', dot: '#f59e0b' },
  expiring: { text: 'Expiring', bg: '#fffbeb', border: '#fde68a', color: '#92400e', dot: '#f59e0b' },
  too_early: { text: 'Not legal yet', bg: '#fffbeb', border: '#fde68a', color: '#92400e', dot: '#f59e0b' },
  expired: { text: 'Expired', bg: '#fef2f2', border: '#fecaca', color: '#b91c1c', dot: '#ef4444' },
  missing: { text: 'Needed', bg: '#fef2f2', border: '#fecaca', color: '#b91c1c', dot: '#ef4444' },
};

function Locate811Chip({ locate, labeled = false }) {
  if (!locate) return labeled ? null : <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>;
  const c = LOCATE_811_CHIP[locate.state] || LOCATE_811_CHIP.waiting;
  const tip = [locate.label, locate.required_reason].filter(Boolean).join(' · ');
  return (
    <span title={tip} style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap',
      fontSize: '12px', fontWeight: 600, padding: '3px 8px', borderRadius: '10px',
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {labeled ? `811 ${c.text}` : c.text}
    </span>
  );
}

// Small badge that shows the worst-case QC status across all checklists for a job.
// Renders nothing if the job has no checklists yet.
function JobQCBadge({ status, compact = false }) {
  if (!status) return null;
  const config = {
    in_progress: {
      label: compact ? 'QC' : 'QC In Progress',
      bg: '#fef3c7', color: '#92400e', border: '#fcd34d', icon: FiClock,
    },
    revisions_required: {
      label: compact ? 'QC: Rev' : 'QC: Revisions',
      bg: '#fee2e2', color: '#991b1b', border: '#fca5a5', icon: FiAlertCircle,
    },
    approved: {
      label: compact ? 'QC \u2713' : 'QC Approved',
      bg: '#d1fae5', color: '#065f46', border: '#6ee7b7', icon: FiCheckCircle,
    },
  }[status];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <Link
      to="/qc"
      onClick={(e) => e.stopPropagation()}
      title={`Open QC dashboard (status: ${status.replace('_', ' ')})`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 8px', borderRadius: '12px',
        fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px',
        backgroundColor: config.bg, color: config.color, border: `1px solid ${config.border}`,
        textDecoration: 'none', whiteSpace: 'nowrap',
      }}
    >
      <Icon size={11} /> {config.label}
    </Link>
  );
}

function Jobs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManagePayroll = user?.can_manage_payroll || false;
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [statusFilter, setStatusFilter] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('status') || 'All';
  });
  const [specialFilter, setSpecialFilter] = useState(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('mine') === 'true') return 'mine';
    if (sp.get('overdue') === 'true') return 'overdue';
    if (sp.get('over_budget') === 'true') return 'over_budget';
    return null;
  }); // 'mine' | 'overdue' | 'over_budget' (from dashboard tiles)
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const lastLocationKey = useRef(location.key);
  const loadSeq = useRef(0); // guards against out-of-order job loads clobbering results
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('jobsViewMode') || 'grid';
  });

  // Sort state
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('jobsSortBy') || 'created_at';
  });
  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('jobsSortOrder') || 'desc';
  });

  // Pagination state
  const [totalJobs, setTotalJobs] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const searchInputRef = useRef(null);
  const isTypingRef = useRef(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    county: '',
    land_lot: '',
    district: '',
    section: '',
    subdivision: '',
    parcel: ''
  });

  // Merge state
  const [mergeSourceJob, setMergeSourceJob] = useState(null);
  const [mergeTargetSearch, setMergeTargetSearch] = useState('');
  const [mergeTargetId, setMergeTargetId] = useState(null);
  const [mergePreview, setMergePreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const searchFields = [
    { value: 'all', label: 'All Fields' },
    { value: 'job_number', label: 'Job Number' },
    { value: 'property_address', label: 'Address' },
    { value: 'client_name', label: 'Client' },
    { value: 'county', label: 'County' },
    { value: 'land_lot', label: 'Land Lot' },
    { value: 'district', label: 'District' },
    { value: 'section', label: 'Section' },
    { value: 'subdivision', label: 'Subdivision' },
    { value: 'parcel', label: 'Parcel' }
  ];

  const statuses = [
    'All',
    'Pending',
    'Scheduled',
    'In Progress - Office Work',
    'In Progress - Initial Field Work',
    'In Progress - Topo',
    'In Progress - Additional Field Work Required',
    'In Progress - Drafting',
    'On Hold',
    'Survey Complete',
    'Needs Review',
    'Sent for Review',
    // Filter only — 'Approved' was retired 2026-08-21 (too vague: approved by whom?) and is
    // no longer offered when SETTING a status, but jobs still sitting on it must stay
    // findable here. The mobile filter shows a live count, so it reads 0 once they're moved.
    'Approved',
    'Approved - Internal Review',
    'Approved - Planning & Zoning',
    'Submitted for Recording',
    'Recorded',
    'Final Mark Up / Stake Lines',
    'Needs Invoice',
    'Invoiced',
    'Paid',
    'Cancelled'
  ];

  useEffect(() => {
    const checkMobile = () => {
      const isCapacitor = window.Capacitor?.isNativePlatform?.() || false;
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isCapacitor || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Debounce search term
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Maintain focus on search input while typing
  useEffect(() => {
    if (isTypingRef.current && searchInputRef.current) {
      // Use setTimeout to ensure this runs after React's render cycle
      const focusTimeout = setTimeout(() => {
        if (isTypingRef.current && searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);
      return () => clearTimeout(focusTimeout);
    }
  }, [jobs, filteredJobs, loading]);

  // Load jobs with server-side search, pagination, and sorting
  const loadJobs = useCallback(async (reset = false, loadMore = false, isSearch = false) => {
    const seq = ++loadSeq.current;
    try {
      // Only show full loading screen on initial load, not during search
      if (!loadMore && !isSearch) setLoading(true);
      if (isSearch) setIsSearching(true);
      const offset = reset ? 0 : jobs.length;

      // Build query params
      const params = new URLSearchParams();
      if (debouncedSearchTerm.trim()) params.append('search', debouncedSearchTerm.trim());
      if (statusFilter && statusFilter !== 'All') params.append('status', statusFilter);
      if (specialFilter === 'mine') params.append('mine', 'true');
      if (specialFilter === 'overdue') params.append('overdue', 'true');
      if (specialFilter === 'over_budget') params.append('over_budget', 'true');
      params.append('limit', JOBS_PER_PAGE);
      params.append('offset', offset);
      // Send sorting parameters to server
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      // Send advanced filters to server
      if (advancedFilters.county.trim()) params.append('county', advancedFilters.county.trim());
      if (advancedFilters.land_lot.trim()) params.append('land_lot', advancedFilters.land_lot.trim());
      if (advancedFilters.district.trim()) params.append('district', advancedFilters.district.trim());
      if (advancedFilters.section.trim()) params.append('section', advancedFilters.section.trim());
      if (advancedFilters.subdivision.trim()) params.append('subdivision', advancedFilters.subdivision.trim());
      if (advancedFilters.parcel.trim()) params.append('parcel', advancedFilters.parcel.trim());

      const response = await getJobs(params.toString());
      // Ignore stale responses — only the most recent load may apply its results
      if (seq !== loadSeq.current) return;
      const jobsData = response.data.jobs || [];
      const total = response.data.total || 0;

      if (reset) {
        setJobs(jobsData);
        setFilteredJobs(jobsData);
        setCurrentPage(0);
      } else {
        const newJobs = [...jobs, ...jobsData];
        setJobs(newJobs);
        setFilteredJobs(newJobs);
        setCurrentPage(prev => prev + 1);
      }

      setTotalJobs(total);
      setHasMore(offset + jobsData.length < total);
      setLoading(false);
      setIsSearching(false);
    } catch (error) {
      if (seq !== loadSeq.current) return; // a newer load is in progress
      console.error('Error loading jobs:', error);
      setJobs([]);
      setFilteredJobs([]);
      setLoading(false);
      setIsSearching(false);
    }
  }, [debouncedSearchTerm, statusFilter, specialFilter, jobs.length, sortBy, sortOrder, advancedFilters]);

  // Read filter params from the URL (dashboard "Needs Attention" tiles link here)
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    setStatusFilter(sp.get('status') || 'All');
    if (sp.get('mine') === 'true') setSpecialFilter('mine');
    else if (sp.get('overdue') === 'true') setSpecialFilter('overdue');
    else if (sp.get('over_budget') === 'true') setSpecialFilter('over_budget');
    else setSpecialFilter(null);
  }, [location.search]);

  useEffect(() => {
    // Pass isSearch=true when triggered by search term or filter change
    const isSearchTriggered = debouncedSearchTerm !== '' || Object.values(advancedFilters).some(v => v.trim());
    loadJobs(true, false, isSearchTriggered);
  }, [debouncedSearchTerm, statusFilter, specialFilter, sortBy, sortOrder, advancedFilters]);

  // Reload jobs when navigating back to this page (e.g., after editing on JobDetail page)
  useEffect(() => {
    if (lastLocationKey.current !== location.key) {
      lastLocationKey.current = location.key;
      loadJobs(true, false, false);
    }
  }, [location.key]);

  useEffect(() => {
    localStorage.setItem('jobsViewMode', viewMode);
  }, [viewMode]);

  // Save sort preferences
  useEffect(() => {
    localStorage.setItem('jobsSortBy', sortBy);
    localStorage.setItem('jobsSortOrder', sortOrder);
  }, [sortBy, sortOrder]);

  // Apply client-side advanced filters (sorting is handled server-side now)
  useEffect(() => {
    if (!advancedFilters.county && !advancedFilters.land_lot && !advancedFilters.district &&
        !advancedFilters.section && !advancedFilters.subdivision && !advancedFilters.parcel) {
      // Jobs are already sorted by server, just sync to filteredJobs
      setFilteredJobs(jobs);
      return;
    }
    filterJobs();
  }, [jobs, advancedFilters]);

  const filterJobs = () => {
    let result = jobs;
    
    // Apply status filter
    if (statusFilter !== 'All') {
      result = result.filter(job => job.status === statusFilter);
    }
    
    // Apply basic search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(job => {
        if (searchField === 'all') {
          // Search across all relevant fields
          return (
            job.job_number?.toLowerCase().includes(term) ||
            job.property_address?.toLowerCase().includes(term) ||
            job.client_name?.toLowerCase().includes(term) ||
            job.county?.toLowerCase().includes(term) ||
            job.land_lot?.toLowerCase().includes(term) ||
            job.district?.toLowerCase().includes(term) ||
            job.section?.toLowerCase().includes(term) ||
            job.subdivision?.toLowerCase().includes(term) ||
            job.parcel?.toLowerCase().includes(term) ||
            job.location_city_state_zip?.toLowerCase().includes(term) ||
            job.description_of_work?.toLowerCase().includes(term)
          );
        } else {
          // Search specific field
          const fieldValue = job[searchField];
          return fieldValue?.toLowerCase().includes(term);
        }
      });
    }
    
    // Apply advanced filters (AND logic - all must match)
    if (advancedFilters.county.trim()) {
      result = result.filter(job => 
        job.county?.toLowerCase().includes(advancedFilters.county.toLowerCase().trim())
      );
    }
    if (advancedFilters.land_lot.trim()) {
      result = result.filter(job => 
        job.land_lot?.toLowerCase().includes(advancedFilters.land_lot.toLowerCase().trim())
      );
    }
    if (advancedFilters.district.trim()) {
      result = result.filter(job => 
        job.district?.toLowerCase().includes(advancedFilters.district.toLowerCase().trim())
      );
    }
    if (advancedFilters.section.trim()) {
      result = result.filter(job => 
        job.section?.toLowerCase().includes(advancedFilters.section.toLowerCase().trim())
      );
    }
    if (advancedFilters.subdivision.trim()) {
      result = result.filter(job => 
        job.subdivision?.toLowerCase().includes(advancedFilters.subdivision.toLowerCase().trim())
      );
    }
    if (advancedFilters.parcel.trim()) {
      result = result.filter(job =>
        job.parcel?.toLowerCase().includes(advancedFilters.parcel.toLowerCase().trim())
      );
    }

    // Server handles sorting, just set the filtered results
    setFilteredJobs(result);
  };

  const clearAdvancedFilters = () => {
    setAdvancedFilters({
      county: '',
      land_lot: '',
      district: '',
      section: '',
      subdivision: '',
      parcel: ''
    });
  };
  
  const hasAdvancedFilters = Object.values(advancedFilters).some(v => v.trim());

  const handleDelete = async (e, jobId) => {
    e.stopPropagation();
    e.preventDefault();
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await deleteJob(jobId);
        loadJobs(true);
      } catch (error) {
        console.error('Error deleting job:', error);
        const errorMessage = error.response?.data?.error || 'Failed to delete job';
        alert(errorMessage);
      }
    }
  };

  const handleEdit = (e, job) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingJob(job);
    setShowForm(true);
  };

  const openMergeModal = (e, job) => {
    e.stopPropagation();
    e.preventDefault();
    setMergeSourceJob(job);
    setMergeTargetSearch('');
    setMergeTargetId(null);
    setMergePreview(null);
  };

  const loadMergePreview = async (targetId) => {
    if (!mergeSourceJob || !targetId) return;
    setLoadingPreview(true);
    try {
      const response = await getJobMergePreview(mergeSourceJob.id, targetId);
      setMergePreview(response.data);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to load merge preview');
    }
    setLoadingPreview(false);
  };

  const handleMerge = async () => {
    if (!mergeSourceJob || !mergeTargetId || !mergePreview) return;
    const confirmMsg = `Are you sure you want to merge "${mergeSourceJob.job_number}" into "${mergePreview.target_job.job_number}"?\n\nThis will move all data to the target job and DELETE the source job. This cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const response = await mergeJob(mergeSourceJob.id, mergeTargetId);
      alert(response.data.message);
      setMergeSourceJob(null);
      setMergePreview(null);
      setMergeTargetId(null);
      loadJobs(true);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to merge jobs');
    }
  };

  const handleJobClick = (job) => {
    // Go straight to the job instead of opening the quick-view popup.
    navigate(`/jobs/${job.id}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': '#fbbf24',
      'Scheduled': '#60a5fa',
      'In Progress - Initial Field Work': '#a78bfa',
      'In Progress - Topo': '#6366f1',
      'In Progress - Additional Field Work Required': '#7c3aed',
      'In Progress - Drafting': '#8b5cf6',
      'Survey Complete': '#34d399',
      'Needs Review': '#f97316',
      'Sent for Review': '#f59e0b',
      'Approved': '#22c55e',
      'Approved - Internal Review': '#4ade80',
      'Approved - Planning & Zoning': '#22c55e',
      'Submitted for Recording': '#14b8a6',
      'Recorded': '#0d9488',
      'Final Mark Up / Stake Lines': '#ec4899',
      'Needs Invoice': '#eab308',
      'Invoiced': '#fb923c',
      'Paid': '#10b981',
      'On Hold': '#64748b'
    };
    return colors[status] || '#6b7280';
  };

  const getShortStatus = (status) => {
    const short = {
      'In Progress - Initial Field Work': 'Initial Field',
      'In Progress - Topo': 'Topo',
      'In Progress - Additional Field Work Required': 'Add\'l Field',
      'In Progress - Drafting': 'Drafting',
      'Survey Complete': 'Complete',
      'Needs Review': 'Review',
      'Sent for Review': 'In Review',
      'Approved': 'Approved',
      'Approved - Internal Review': 'Approved (Ours)',
      'Approved - Planning & Zoning': 'Approved (P&Z)',
      'Submitted for Recording': 'For Recording',
      'Final Mark Up / Stake Lines': 'Stake Lines',
      'Needs Invoice': 'Needs Invoice'
    };
    return short[status] || status;
  };

  // Only show full loading screen on initial load (no jobs yet)
  if (loading && jobs.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50vh',
        color: '#666'
      }}>
        Loading jobs...
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div style={{ padding: '5px' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px'
        }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>Jobs</h1>
          <button
            onClick={() => {
              setEditingJob(null);
              setShowForm(true);
            }}
            style={{
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 16px',
              fontSize: '15px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              minHeight: '44px'
            }}
          >
            <FiPlus size={18} /> New
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '10px' }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'stretch'
          }}>
            <div style={{
              flex: 1,
              position: 'relative'
            }}>
              <FiSearch style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`Search ${searchFields.find(f => f.value === searchField)?.label || 'jobs'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => { isTypingRef.current = true; }}
                onBlur={() => { isTypingRef.current = false; }}
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 40px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '15px',
                  minHeight: '48px'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    padding: '4px'
                  }}
                >
                  <FiX size={18} />
                </button>
              )}
            </div>
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              style={{
                padding: '12px',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '14px',
                backgroundColor: 'white',
                minWidth: '100px'
              }}
            >
              {searchFields.map(field => (
                <option key={field.value} value={field.value}>{field.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter Button */}
        <button
          onClick={() => setShowFilterMenu(true)}
          style={{
            width: '100%',
            padding: '12px 16px',
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            marginBottom: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            minHeight: '48px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiFilter color="#666" />
            <span style={{ color: '#333' }}>
              {statusFilter === 'All' ? 'All Jobs' : statusFilter}
            </span>
          </div>
          <span style={{
            backgroundColor: '#e5e7eb',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '13px',
            color: '#666'
          }}>
            {isSearching ? '...' : filteredJobs.length}
          </span>
        </button>

        {/* Sort Controls - Mobile */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '15px'
        }}>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              flex: 1,
              padding: '12px',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              fontSize: '14px',
              backgroundColor: 'white'
            }}
          >
            <option value="created_at">Sort by Created</option>
            <option value="scheduled_date">Sort by Scheduled</option>
            <option value="last_worked">Sort by Last Worked</option>
            <option value="job_number">Sort by Job #</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            style={{
              padding: '12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '10px',
              backgroundColor: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px'
            }}
          >
            {sortOrder === 'asc' ? <FiArrowUp size={16} /> : <FiArrowDown size={16} />}
            {sortOrder === 'asc' ? 'Old' : 'New'}
          </button>
        </div>

        {/* Filter Modal */}
        {showFilterMenu && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-end'
          }}>
            <div style={{
              backgroundColor: 'white',
              width: '100%',
              borderRadius: '20px 20px 0 0',
              padding: '20px',
              paddingBottom: 'max(20px, env(safe-area-inset-bottom))'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px'
              }}>
                <h3 style={{ margin: 0, fontSize: '18px' }}>Filter by Status</h3>
                <button
                  onClick={() => setShowFilterMenu(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '10px',
                    cursor: 'pointer'
                  }}
                >
                  <FiX size={24} />
                </button>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {statuses.map(status => {
                  const count = status === 'All' 
                    ? jobs.length 
                    : jobs.filter(j => j.status === status).length;
                  
                  return (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setShowFilterMenu(false);
                      }}
                      style={{
                        padding: '14px 16px',
                        backgroundColor: statusFilter === status ? '#e0e7ff' : '#f8fafc',
                        border: statusFilter === status ? '2px solid #2563eb' : '1px solid #e5e7eb',
                        borderRadius: '10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        minHeight: '50px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {status !== 'All' && (
                          <div style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(status)
                          }} />
                        )}
                        <span style={{ 
                          fontWeight: statusFilter === status ? '600' : '400',
                          color: '#333'
                        }}>
                          {status}
                        </span>
                      </div>
                      <span style={{ color: '#666' }}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Jobs List */}
        {filteredJobs.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filteredJobs.map(job => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                state={{ from: '/jobs', label: 'Jobs' }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '16px',
                  textDecoration: 'none',
                  color: 'inherit',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '8px'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: '600', 
                      fontSize: '16px',
                      color: '#333',
                      marginBottom: '4px'
                    }}>
                      {job.job_number}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {job.client_name}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {job.priority && job.priority !== 'Normal' && (
                      <span style={{
                        padding: '3px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: '700',
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        color: job.priority === 'Urgent' ? '#dc2626' : job.priority === 'High' ? '#ea580c' : '#6b7280',
                        backgroundColor: job.priority === 'Urgent' ? '#fef2f2' : job.priority === 'High' ? '#fff7ed' : '#f9fafb',
                        border: `1px solid ${job.priority === 'Urgent' ? '#fecaca' : job.priority === 'High' ? '#fed7aa' : '#e5e7eb'}`
                      }}>
                        {job.priority}
                      </span>
                    )}
                    <JobQCBadge status={job.qc_status} />
                    <Locate811Chip locate={job.locate_811} labeled />
                    <div style={{
                      backgroundColor: getStatusColor(job.status),
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {getShortStatus(job.status)}
                    </div>
                  </div>
                </div>

                {job.property_address && (
                  <div style={{
                    fontSize: '13px',
                    color: '#888',
                    marginBottom: '8px'
                  }}>
                    📍 {job.property_address}
                  </div>
                )}

                {/* Parcel Link - use saved URL if available, otherwise county search */}
                {(job.parcel_url || hasQPublicUrl(job.county)) && (
                  <a
                    href={job.parcel_url || buildQPublicUrl(job.county)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      color: job.parcel_url ? '#166534' : '#ea580c',
                      textDecoration: 'none',
                      marginBottom: '8px',
                      padding: '5px 10px',
                      backgroundColor: job.parcel_url ? '#dcfce7' : '#fff7ed',
                      borderRadius: '8px',
                      border: job.parcel_url ? '1px solid #86efac' : '1px solid #fed7aa',
                      width: 'fit-content'
                    }}
                  >
                    <FiExternalLink size={12} />
                    {job.parcel_url ? 'View Parcel' : `Search Parcel (${job.county})`}
                  </a>
                )}

                {/* Client Contact - Tap to Call */}
                {job.client_phone && (
                  <a
                    href={`tel:${job.client_phone}`}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '13px',
                      color: '#2563eb',
                      textDecoration: 'none',
                      marginBottom: '8px',
                      padding: '6px 10px',
                      backgroundColor: '#eff6ff',
                      borderRadius: '8px',
                      width: 'fit-content'
                    }}
                  >
                    📞 {job.client_phone}
                  </a>
                )}

                {/* Team Members */}
                {job.team_members && job.team_members.length > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '6px',
                    marginBottom: '8px'
                  }}>
                    {job.team_members.map(tm => (
                      <span
                        key={tm.id}
                        style={{
                          backgroundColor: '#e5e7eb',
                          padding: '3px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          color: '#555'
                        }}
                      >
                        {tm.name}
                      </span>
                    ))}
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingTop: '8px',
                  borderTop: '1px solid #f0f0f0'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {job.scheduled_date ? (
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        📅 {new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString()}
                      </span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#999' }}>Not scheduled</span>
                    )}
                    {job.last_worked_date && (
                      <span style={{ fontSize: '11px', color: '#6b7280' }}>
                        Last: {new Date(job.last_worked_date).toLocaleDateString()}{job.last_worked_by ? ` - ${job.last_worked_by}` : ''}
                      </span>
                    )}
                  </div>
                  <FiChevronRight color="#ccc" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            backgroundColor: 'white',
            borderRadius: '12px'
          }}>
            <FiBriefcase size={48} color="#ddd" style={{ marginBottom: '15px' }} />
            <h3 style={{ margin: '0 0 10px', color: '#666' }}>
              {statusFilter === 'All' ? 'No Jobs Yet' : `No ${statusFilter} Jobs`}
            </h3>
            <p style={{ margin: 0, color: '#999', fontSize: '14px' }}>
              {statusFilter === 'All'
                ? 'Tap the + button to create one'
                : 'Try a different filter'
              }
            </p>
          </div>
        )}

        {/* Load More Button - Mobile */}
        {hasMore && filteredJobs.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '15px',
            marginBottom: '15px'
          }}>
            <button
              onClick={() => loadJobs(false, true)}
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                minHeight: '48px'
              }}
            >
              {loading ? 'Loading...' : `Load More (${totalJobs - filteredJobs.length} remaining)`}
            </button>
          </div>
        )}

        {/* Job Form Modal */}
        {showForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'white',
            zIndex: 1000,
            overflowY: 'auto'
          }}>
            <JobForm
              job={editingJob}
              onSuccess={() => {
                setShowForm(false);
                setEditingJob(null);
                loadJobs(true);
              }}
              onCancel={() => {
                setShowForm(false);
                setEditingJob(null);
              }}
            />
          </div>
        )}
      </div>
    );
  }

  // Desktop Layout (unchanged)
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const parts = timeString.split(':');
      const hours = parseInt(parts[0], 10);
      const minutes = parts.length > 1 ? parts[1] : '00';

      if (Number.isNaN(hours)) {
        return timeString;
      }

      const date = new Date();
      date.setHours(hours);
      date.setMinutes(parseInt(minutes, 10));
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch (e) {
      return timeString;
    }
  };

  return (
    <div className="jobs-page">
      <div className="page-header">
        <div>
          <h1>Jobs</h1>
          <p className="subtitle">Manage your survey jobs</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setEditingJob(null);
            setShowForm(!showForm);
          }}
        >
          <FiPlus /> {showForm ? 'Cancel' : 'New Job'}
        </button>
      </div>

      {specialFilter && (() => {
        const themes = {
          mine: { label: 'My Jobs', bg: '#eef2ff', border: '#c7d2fe', text: '#4338ca' },
          overdue: { label: 'Overdue Jobs', bg: '#fef2f2', border: '#fecaca', text: '#b91c1c' },
          over_budget: { label: 'Over Budget Jobs', bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
        };
        const t = themes[specialFilter] || themes.overdue;
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '12px', padding: '10px 16px', marginBottom: '15px',
            background: t.bg, border: `1px solid ${t.border}`, borderRadius: '10px'
          }}>
            <span style={{ fontWeight: 600, color: t.text }}>
              Showing: {t.label} ({filteredJobs.length})
            </span>
            <Link to="/jobs" style={{ color: '#2563eb', textDecoration: 'none', fontSize: '14px', fontWeight: 500 }}>
              ✕ Clear filter
            </Link>
          </div>
        );
      })()}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <JobForm
              job={editingJob}
              onSuccess={() => {
                setShowForm(false);
                setEditingJob(null);
                loadJobs(true);
              }}
              onCancel={() => {
                setShowForm(false);
                setEditingJob(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '250px' }}>
            <FiSearch style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af'
            }} />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => { isTypingRef.current = true; }}
              onBlur={() => { isTypingRef.current = false; }}
              style={{
                width: '100%',
                padding: '12px 40px 12px 44px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '15px'
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9ca3af',
                  padding: '4px'
                }}
              >
                <FiX size={18} />
              </button>
            )}
          </div>
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer',
              minWidth: '140px'
            }}
          >
            {searchFields.map(field => (
              <option key={field.value} value={field.value}>{field.label}</option>
            ))}
          </select>
          {(searchTerm || isSearching) && (
            <span style={{
              color: '#666',
              fontSize: '14px',
              backgroundColor: '#f3f4f6',
              padding: '8px 12px',
              borderRadius: '6px'
            }}>
              {isSearching ? 'Searching...' : `${filteredJobs.length} result${filteredJobs.length !== 1 ? 's' : ''}`}
            </span>
          )}
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            style={{
              padding: '12px 16px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: showAdvancedSearch || hasAdvancedFilters ? '#2563eb' : 'white',
              color: showAdvancedSearch || hasAdvancedFilters ? 'white' : '#374151',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <FiFilter size={16} />
            Advanced
            {hasAdvancedFilters && (
              <span style={{
                backgroundColor: 'white',
                color: '#2563eb',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600'
              }}>
                {Object.values(advancedFilters).filter(v => v.trim()).length}
              </span>
            )}
          </button>
          
          {/* View Toggle */}
          <div style={{
            display: 'flex',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '10px 14px',
                border: 'none',
                backgroundColor: viewMode === 'grid' ? '#2563eb' : 'white',
                color: viewMode === 'grid' ? 'white' : '#666',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <FiGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '10px 14px',
                border: 'none',
                borderLeft: '1px solid #e5e7eb',
                backgroundColor: viewMode === 'list' ? '#2563eb' : 'white',
                color: viewMode === 'list' ? 'white' : '#666',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <FiList size={18} />
            </button>
          </div>

          {/* Sort Controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '8px 10px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '13px',
                backgroundColor: 'white',
                cursor: 'pointer',
                width: '150px'
              }}
            >
              <option value="created_at">Date Created</option>
              <option value="scheduled_date">Scheduled Date</option>
              <option value="last_worked">Last Worked</option>
              <option value="job_number">Job Number</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              style={{
                padding: '8px',
                width: '36px',
                height: '36px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                backgroundColor: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#374151',
                flexShrink: 0
              }}
              title={sortOrder === 'asc' ? 'Oldest first' : 'Newest first'}
            >
              {sortOrder === 'asc' ? <FiArrowUp size={16} /> : <FiArrowDown size={16} />}
            </button>
          </div>
        </div>

        {/* Advanced Search Panel */}
        {showAdvancedSearch && (
          <div style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px'
            }}>
              <span style={{ fontWeight: '500', color: '#374151' }}>
                Filter by Legal Description
              </span>
              {hasAdvancedFilters && (
                <button
                  onClick={clearAdvancedFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '12px'
            }}>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>County</label>
                <input
                  type="text"
                  placeholder="e.g. Gordon"
                  value={advancedFilters.county}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, county: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Land Lot</label>
                <input
                  type="text"
                  placeholder="e.g. 123"
                  value={advancedFilters.land_lot}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, land_lot: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>District</label>
                <input
                  type="text"
                  placeholder="e.g. 15"
                  value={advancedFilters.district}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, district: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Section</label>
                <input
                  type="text"
                  placeholder="e.g. 3"
                  value={advancedFilters.section}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, section: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Subdivision</label>
                <input
                  type="text"
                  placeholder="e.g. Oak Hills"
                  value={advancedFilters.subdivision}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, subdivision: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>Parcel</label>
                <input
                  type="text"
                  placeholder="e.g. 001-234"
                  value={advancedFilters.parcel}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, parcel: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="filter-bar">
        <FiFilter />
        <span>Filter by status:</span>
        <div className="filter-buttons">
          {statuses.map(status => (
            <button
              key={status}
              className={`filter-btn ${statusFilter === status ? 'active' : ''}`}
              onClick={() => setStatusFilter(status)}
            >
              {status}
              {status === 'All' && ` (${jobs.length})`}
              {status !== 'All' && ` (${jobs.filter(j => j.status === status).length})`}
            </button>
          ))}
        </div>
      </div>

      {/* Jobs count and pagination info */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px',
        color: '#666',
        fontSize: '14px'
      }}>
        <span>Showing {filteredJobs.length} of {totalJobs} jobs</span>
      </div>

      {filteredJobs.length > 0 ? (
        viewMode === 'grid' ? (
          // Grid View
          <div className="jobs-grid">
            {filteredJobs.map(job => (
              <div 
                key={job.id} 
                className="job-card"
                onClick={() => handleJobClick(job)}
                style={{ cursor: 'pointer' }}
              >
                <div className="job-card-header">
                  <h3>{job.job_number}</h3>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <JobQCBadge status={job.qc_status} compact />
                    <Locate811Chip locate={job.locate_811} labeled />
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(job.status) }}
                    >
                      {job.status}
                    </span>
                  </div>
                </div>
                
                <p className="job-client-name">{job.client_name}</p>
                
                {/* Client Contact */}
                {(job.client_phone || job.client_email) && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    fontSize: '13px',
                    marginTop: '4px',
                    marginBottom: '4px'
                  }}>
                    {job.client_phone && (
                      <a 
                        href={`tel:${job.client_phone}`}
                        style={{ color: '#2563eb', textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        📞 {job.client_phone}
                      </a>
                    )}
                    {job.client_email && (
                      <a 
                        href={`mailto:${job.client_email}`}
                        style={{ color: '#2563eb', textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        ✉️ Email
                      </a>
                    )}
                  </div>
                )}
                
                {job.property_address && (
                  <p className="job-property">{job.property_address}</p>
                )}

                {job.team_members && job.team_members.length > 0 && (
                  <div className="job-team" style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '4px', 
                    marginTop: '8px' 
                  }}>
                    {job.team_members.map(tm => (
                      <span 
                        key={tm.id} 
                        style={{
                          backgroundColor: '#e5e7eb',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          color: '#374151'
                        }}
                      >
                        {tm.name}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="job-card-footer">
                  {job.scheduled_date && (
                    <span className="job-date">
                      📅 {new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString()}
                      {job.scheduled_time && ` at ${formatTime(job.scheduled_time)}`}
                    </span>
                  )}
                  {job.last_worked_date && (
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      Last worked: {new Date(job.last_worked_date).toLocaleDateString()}{job.last_worked_by ? ` - ${job.last_worked_by}` : ''}
                    </span>
                  )}
                  <span className="job-created">
                    Created {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>

                <div 
                  className="job-card-actions"
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '1px solid #e5e7eb'
                  }}
                >
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => handleEdit(e, job)}
                    style={{ flex: 1, padding: '6px 12px', fontSize: '13px' }}
                  >
                    <FiEdit /> Edit
                  </button>
                  {canManagePayroll && (
                    <button
                      className="btn btn-sm"
                      onClick={(e) => openMergeModal(e, job)}
                      style={{ padding: '6px 12px', fontSize: '13px', backgroundColor: '#fff3e0', border: '1px solid #ffcc80', color: '#e65100' }}
                    >
                      <FiGitMerge />
                    </button>
                  )}
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={(e) => handleDelete(e, job.id)}
                    style={{ padding: '6px 12px', fontSize: '13px' }}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // List View
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '13%', overflow: 'hidden', textOverflow: 'ellipsis' }}>Job #</th>
                  <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '12%', overflow: 'hidden', textOverflow: 'ellipsis' }}>Client</th>
                  <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '11%', overflow: 'hidden', textOverflow: 'ellipsis' }}>Property Address</th>
                  <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '8%' }}>County</th>
                  <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '8%' }}>Parcel</th>
                  <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '12%' }}>Status</th>
                  <th title="Georgia 811 locate ticket" style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '8%' }}>811</th>
                  <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '8%' }}>Scheduled</th>
                  <th style={{ padding: '14px 12px', textAlign: 'left', fontWeight: '600', color: '#374151', width: '8%' }}>Last Worked</th>
                  <th style={{ padding: '14px 12px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '12%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map(job => (
                  <tr 
                    key={job.id} 
                    style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                    onClick={() => handleJobClick(job)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <td title={job.job_number} style={{ padding: '10px 12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Link
                        to={`/jobs/${job.id}`}
                        state={{ from: '/jobs', label: 'Jobs' }}
                        style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {job.job_number}
                      </Link>
                    </td>
                    <td title={job.client_name} style={{ padding: '10px 12px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.client_name}</div>
                      {job.client_phone && (
                        <a
                          href={`tel:${job.client_phone}`}
                          style={{ color: '#2563eb', textDecoration: 'none', fontSize: '13px' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {job.client_phone}
                        </a>
                      )}
                    </td>
                    <td title={job.property_address || ''} style={{ padding: '10px 12px', color: '#666', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {job.property_address || '-'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#666', fontSize: '14px' }}>
                      {job.county || '-'}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {(job.parcel_url || hasQPublicUrl(job.county)) ? (
                        <a
                          href={job.parcel_url || buildQPublicUrl(job.county)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            textDecoration: 'none',
                            color: job.parcel_url ? '#166534' : '#ea580c',
                            backgroundColor: job.parcel_url ? '#dcfce7' : '#fff7ed',
                            border: job.parcel_url ? '1px solid #86efac' : '1px solid #fed7aa',
                          }}
                        >
                          <FiExternalLink size={12} />
                          {job.parcel_url ? 'View' : 'Search'}
                        </a>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        <span style={{
                          backgroundColor: getStatusColor(job.status),
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          whiteSpace: 'nowrap'
                        }}>
                          {getShortStatus(job.status)}
                        </span>
                        <JobQCBadge status={job.qc_status} compact />
                      </div>
                    </td>
                    <td style={{ padding: '14px 8px' }}>
                      <Locate811Chip locate={job.locate_811} />
                    </td>
                    <td style={{ padding: '14px 16px', color: '#666', fontSize: '14px' }}>
                      {job.scheduled_date
                        ? new Date(job.scheduled_date + 'T00:00:00').toLocaleDateString()
                        : '-'
                      }
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px' }}>
                      {job.last_worked_date ? (
                        <div>
                          <div style={{ color: '#374151' }}>
                            {new Date(job.last_worked_date).toLocaleDateString()}
                          </div>
                          {job.last_worked_by && (
                            <div style={{ color: '#6b7280', fontSize: '12px' }}>
                              {job.last_worked_by}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={(e) => handleEdit(e, job)}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#e5e7eb',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#374151',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <FiEdit size={14} /> Edit
                        </button>
                        {canManagePayroll && (
                          <button
                            onClick={(e) => openMergeModal(e, job)}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#fff3e0',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#e65100',
                              cursor: 'pointer',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Merge into another job"
                          >
                            <FiGitMerge size={14} />
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDelete(e, job.id)}
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#fee2e2',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="empty-state">
          <FiBriefcase size={64} />
          <h2>
            {statusFilter === 'All' ? 'No Jobs Yet' : `No ${statusFilter} Jobs`}
          </h2>
          <p>
            {statusFilter === 'All'
              ? 'Create your first job to get started'
              : 'Try selecting a different status filter'
            }
          </p>
          {statusFilter === 'All' && (
            <button
              className="btn btn-primary"
              onClick={() => setShowForm(true)}
            >
              <FiPlus /> Create Your First Job
            </button>
          )}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && filteredJobs.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
          marginBottom: '20px'
        }}>
          <button
            onClick={() => loadJobs(false, true)}
            disabled={loading}
            style={{
              padding: '12px 32px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Loading...' : `Load More (${totalJobs - filteredJobs.length} remaining)`}
          </button>
        </div>
      )}
      {/* Merge Job Modal */}
      {mergeSourceJob && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '24px',
            maxWidth: '550px', width: '100%', maxHeight: '85vh', overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}><FiGitMerge /> Merge Job</h3>
              <button onClick={() => setMergeSourceJob(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px' }}><FiX /></button>
            </div>

            <div style={{ backgroundColor: '#fff3e0', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '13px' }}>
              <strong>Source (will be deleted):</strong><br />
              {mergeSourceJob.job_number} {mergeSourceJob.property_address ? `— ${mergeSourceJob.property_address}` : ''}
            </div>

            <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Merge into:</label>
            <input
              type="text"
              placeholder="Search target job by number or name..."
              value={mergeTargetSearch}
              onChange={(e) => { setMergeTargetSearch(e.target.value); setMergeTargetId(null); setMergePreview(null); }}
              style={{
                width: '100%', padding: '10px', border: '1px solid #ddd',
                borderRadius: '6px', marginBottom: '8px', boxSizing: 'border-box'
              }}
            />
            {mergeTargetSearch.length >= 2 && !mergeTargetId && (
              <div style={{
                maxHeight: '180px', overflowY: 'auto', border: '1px solid #ddd',
                borderRadius: '6px', marginBottom: '16px'
              }}>
                {filteredJobs
                  .filter(j => j.id !== mergeSourceJob.id &&
                    (j.job_number?.toLowerCase().includes(mergeTargetSearch.toLowerCase()) ||
                     j.client_name?.toLowerCase().includes(mergeTargetSearch.toLowerCase()) ||
                     j.property_address?.toLowerCase().includes(mergeTargetSearch.toLowerCase())))
                  .slice(0, 20)
                  .map(j => (
                    <div
                      key={j.id}
                      onClick={() => { setMergeTargetId(j.id); setMergeTargetSearch(j.job_number); loadMergePreview(j.id); }}
                      style={{
                        padding: '10px 12px', cursor: 'pointer',
                        backgroundColor: 'white', borderBottom: '1px solid #f0f0f0', fontSize: '13px'
                      }}
                    >
                      <strong>{j.job_number}</strong>
                      {j.client_name && <span style={{ color: '#666' }}> — {j.client_name}</span>}
                      {j.property_address && <div style={{ color: '#999', fontSize: '12px' }}>{j.property_address}</div>}
                    </div>
                  ))}
              </div>
            )}

            {loadingPreview && <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>Loading preview...</div>}

            {mergePreview && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ backgroundColor: '#e8f5e9', borderRadius: '8px', padding: '12px', marginBottom: '12px', fontSize: '13px' }}>
                  <strong>Target (will keep):</strong><br />
                  {mergePreview.target_job.job_number} {mergePreview.target_job.property_address ? `— ${mergePreview.target_job.property_address}` : ''}
                </div>

                <h4 style={{ margin: '12px 0 8px', fontSize: '14px' }}>Items to transfer:</h4>
                <div style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px', fontSize: '13px' }}>
                  {mergePreview.items_to_move.time_entries > 0 && <div>Time Entries: <strong>{mergePreview.items_to_move.time_entries}</strong> ({mergePreview.items_to_move.total_hours}h)</div>}
                  {mergePreview.items_to_move.work_orders > 0 && <div>Work Orders: <strong>{mergePreview.items_to_move.work_orders}</strong></div>}
                  {mergePreview.items_to_move.change_orders > 0 && <div>Change Orders: <strong>{mergePreview.items_to_move.change_orders}</strong></div>}
                  {mergePreview.items_to_move.expenses > 0 && <div>Expenses: <strong>{mergePreview.items_to_move.expenses}</strong></div>}
                  {mergePreview.items_to_move.comments > 0 && <div>Comments: <strong>{mergePreview.items_to_move.comments}</strong></div>}
                  {mergePreview.items_to_move.photos > 0 && <div>Photos: <strong>{mergePreview.items_to_move.photos}</strong></div>}
                  {mergePreview.items_to_move.parcels > 0 && <div>Parcels: <strong>{mergePreview.items_to_move.parcels}</strong></div>}
                  {mergePreview.items_to_move.invoice_associations > 0 && <div>Invoice Links: <strong>{mergePreview.items_to_move.invoice_associations}</strong></div>}
                  {mergePreview.items_to_move.team_members > 0 && <div>Team Members: <strong>{mergePreview.items_to_move.team_members}</strong></div>}
                  {Object.values(mergePreview.items_to_move).every(v => v === 0) && <div style={{ color: '#999' }}>No items to transfer</div>}
                </div>

                {mergePreview.warnings.length > 0 && (
                  <div style={{ backgroundColor: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', padding: '12px', marginTop: '12px', fontSize: '13px' }}>
                    <FiAlertTriangle style={{ color: '#f9a825', marginRight: '6px' }} />
                    {mergePreview.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => setMergeSourceJob(null)}
                style={{ padding: '10px 20px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleMerge}
                disabled={!mergePreview}
                style={{
                  padding: '10px 20px', backgroundColor: mergePreview ? '#e65100' : '#ccc',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: mergePreview ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}
              >
                <FiGitMerge /> Merge Jobs
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Jobs;